import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { AnimatePresence, motion } from '@utils/framerMotionProxy';
// Слайдер убран: новости показываем по одной, автосмена таймером

import { portfolioAPI, landingAPI, lessonsAPI, shopAPI } from '../services/api';
import ReviewsSection from '../components/ReviewsSection/ReviewsSection';
import OptimizedImage from '../components/UI/OptimizedImage';
import { getImageUrl } from '../utils/urls';
import { normalizeFormattedText } from '../utils/textFormatting';
import i18n from '../i18n';
import { useNavigate } from 'react-router-dom';

const LESSON_VIDEO_SLOT_FIELDS = [
  ['kinescopeVideoUrl', 'kinescopeVideoId', 'kinescopeVideoIdEn', 'kinescopeVideoIdEs'],
  ['kinescopeVideo2Id', 'kinescopeVideo2IdEn', 'kinescopeVideo2IdEs'],
  ['kinescopeVideo3Id', 'kinescopeVideo3IdEn', 'kinescopeVideo3IdEs'],
  ['kinescopeVideo4Id', 'kinescopeVideo4IdEn', 'kinescopeVideo4IdEs'],
  ['kinescopeVideo5Id', 'kinescopeVideo5IdEn', 'kinescopeVideo5IdEs'],
  ['kinescopeVideo6Id', 'kinescopeVideo6IdEn', 'kinescopeVideo6IdEs'],
  ['kinescopeVideo7Id', 'kinescopeVideo7IdEn', 'kinescopeVideo7IdEs'],
  ['kinescopeVideo8Id', 'kinescopeVideo8IdEn', 'kinescopeVideo8IdEs'],
  ['kinescopeVideo9Id', 'kinescopeVideo9IdEn', 'kinescopeVideo9IdEs'],
  ['kinescopeVideo10Id', 'kinescopeVideo10IdEn', 'kinescopeVideo10IdEs']
];

const hasFieldValue = (value) => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return Boolean(value);
};

const getLessonVideoVersionCount = (lesson) => {
  const videoVersions = LESSON_VIDEO_SLOT_FIELDS.reduce((total, slotFields) => {
    const hasVideoInSlot = slotFields.some((field) => hasFieldValue(lesson?.[field]));
    return hasVideoInSlot ? total + 1 : total;
  }, 0);

  // If lesson has no video values yet, still count the lesson itself.
  return videoVersions > 0 ? videoVersions : 1;
};

const getLessonsCounterValue = (lessons) =>
  lessons.reduce((total, lesson) => total + getLessonVideoVersionCount(lesson), 0);

const Landing = () => {
  const { t } = useTranslation();

  const NewsSection = () => {
    const MOBILE_FRAME_DURATION_MS = 6000;
    const DESKTOP_VISIBLE_COUNT = 3;
    const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1024 : false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageDirection, setPageDirection] = useState(0);
    const [desktopStartIndex, setDesktopStartIndex] = useState(0);
    const swipeStartRef = React.useRef(null);

    useEffect(() => {
      const onResize = () => setIsDesktop(window.innerWidth >= 1024);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }, []);

    const { data, isLoading, isError } = useQuery(
      ['landing-news'],
      async () => {
        try {
          return await landingAPI.getNews();
        } catch (err) {
          if (err?.response?.status === 404) {
            return { data: { data: [] } };
          }
          throw err;
        }
      },
      {
        staleTime: 5 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      }
    );

    const news = Array.isArray(data?.data?.data) ? data.data.data : [];
    const slides = news
      .slice()
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const itemsPerPage = isDesktop ? DESKTOP_VISIBLE_COUNT : 1;
    const totalPages = Math.max(1, Math.ceil(slides.length / itemsPerPage));
    const currentPageSafe = Math.min(currentPage, totalPages);
    const paginatedSlides = slides.slice(
      (currentPageSafe - 1) * itemsPerPage,
      currentPageSafe * itemsPerPage
    );
    const maxDesktopStartIndex = Math.max(0, slides.length - DESKTOP_VISIBLE_COUNT);

    useEffect(() => {
      setPageDirection(0);
      setCurrentPage(1);
      setDesktopStartIndex(0);
    }, [isDesktop, slides.length]);

    const handleNextPage = () => {
      setPageDirection(1);
      setCurrentPage((prev) => (prev >= totalPages ? 1 : prev + 1));
    };

    const handlePrevPage = () => {
      setPageDirection(-1);
      setCurrentPage((prev) => (prev <= 1 ? totalPages : prev - 1));
    };

    const handleDesktopNext = () => {
      setDesktopStartIndex((prev) => Math.min(maxDesktopStartIndex, prev + 1));
    };

    const handleDesktopPrev = () => {
      setDesktopStartIndex((prev) => Math.max(0, prev - 1));
    };

    useEffect(() => {
      if (isDesktop || totalPages < 2) return;
      const id = setTimeout(() => {
        setPageDirection(1);
        setCurrentPage((prev) => (prev >= totalPages ? 1 : prev + 1));
      }, MOBILE_FRAME_DURATION_MS);
      return () => clearTimeout(id);
    }, [isDesktop, totalPages, currentPageSafe]);

    const handlePointerDown = (event) => {
      swipeStartRef.current = { x: event.clientX, y: event.clientY };
    };

    const handlePointerUp = (event) => {
      if (!swipeStartRef.current || totalPages < 2) return;

      const deltaX = event.clientX - swipeStartRef.current.x;
      const deltaY = event.clientY - swipeStartRef.current.y;
      swipeStartRef.current = null;

      if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      if (deltaX < 0) {
        handleNextPage();
      } else {
        handlePrevPage();
      }
    };

    const pageVariants = {
      enter: (direction) => ({
        x: direction > 0 ? '130%' : '-130%',
      }),
      center: {
        x: '0%',
      },
      exit: (direction) => ({
        x: direction > 0 ? '-130%' : '130%',
      }),
    };

    if ((!isLoading && slides.length === 0) || isError) {
      return null;
    }

    const getNewsTitle = (item) => {
      const rawTitle = typeof item.title === 'object'
        ? (item.title?.[i18n.language] || '')
        : (item.title || '');
      return normalizeFormattedText(rawTitle);
    };

    const getNewsImageSrc = (item) => (
      item.image
        ? getImageUrl(item.image.startsWith('/uploads/') ? item.image : `/uploads/${item.image}`)
        : '/images/web-images/placeholder.jpg'
    );

    const renderCard = (item, index, wrapperClassName = 'min-w-0') => {
      const title = getNewsTitle(item);
      const imageSrc = getNewsImageSrc(item);
      const hasTitle = Boolean(title && title.trim());

      const imageContent = (
        <OptimizedImage
          src={imageSrc}
          alt={title || 'News'}
          className="w-full h-full object-cover"
          width={800}
          height={450}
          priority
          loading="eager"
        />
      );

      const body = (
        <div className="h-full flex flex-col">
          <div className="relative w-full aspect-square rounded-3xl overflow-hidden bg-gray-50">
            {item.link ? (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={title || t('common.more_details', { defaultValue: 'Подробнее' })}
                className="block w-full h-full"
              >
                {imageContent}
              </a>
            ) : imageContent}
          </div>

          <div className="mt-3 h-[3.5rem] overflow-hidden">
            {hasTitle ? (
              <p className="text-[13px] font-medium text-gray-900 leading-snug break-words">
                <span>{title.trim()}</span>
                {item.link && (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('common.more_details', { defaultValue: 'Подробнее' })}
                    className="inline-flex align-middle ml-1 text-base leading-none text-[#EC8C8E] transition-opacity duration-300 hover:opacity-75"
                  >
                    →
                  </a>
                )}
              </p>
            ) : item.link ? (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('common.more_details', { defaultValue: 'Подробнее' })}
                className="inline-flex self-start text-base leading-none text-[#EC8C8E] transition-opacity duration-300 hover:opacity-75"
              >
                →
              </a>
            ) : null}
          </div>
        </div>
      );

      return (
        <div key={item.id || index} className={wrapperClassName}>
          {body}
        </div>
      );
    };

    const mobileItem = paginatedSlides[0];
    const mobileTitle = mobileItem ? getNewsTitle(mobileItem) : '';
    const mobileImageSrc = mobileItem ? getNewsImageSrc(mobileItem) : '/images/web-images/placeholder.jpg';
    const mobileImageAlt = mobileTitle || 'News';
    const mobileImageKey = mobileItem?.id || `news-mobile-${currentPageSafe}`;

    const hasMobileTitle = Boolean(mobileTitle && mobileTitle.trim());
    const mobileImageContent = (
      <OptimizedImage
        src={mobileImageSrc}
        alt={mobileImageAlt}
        className="w-full h-full object-cover"
        width={800}
        height={450}
        priority
        loading="eager"
      />
    );

    const mobileBody = (
      <div className="h-full flex flex-col">
        <div className="relative w-full aspect-square rounded-3xl overflow-hidden bg-gray-50">
          <AnimatePresence initial={false} mode="sync">
            <motion.div
              key={mobileImageKey}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              style={{ willChange: 'opacity' }}
              className="absolute inset-0"
            >
              {mobileItem?.link ? (
                <a
                  href={mobileItem.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={mobileTitle || t('common.more_details', { defaultValue: 'Подробнее' })}
                  className="block w-full h-full"
                >
                  {mobileImageContent}
                </a>
              ) : mobileImageContent}
            </motion.div>
          </AnimatePresence>

          {totalPages > 1 && (
            <>
              <div className="absolute top-2 left-[20%] right-[20%] z-10">
                <div className="h-0.5 w-full bg-white/35 overflow-hidden rounded-full">
                  <motion.div
                    key={`news-progress-${currentPageSafe}`}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: MOBILE_FRAME_DURATION_MS / 1000, ease: 'linear' }}
                    className="h-full w-full origin-left bg-white"
                  />
                </div>
              </div>

              <div className="absolute left-1/2 -translate-x-1/2 bottom-2 z-10 flex items-center gap-1.5">
                {Array.from({ length: totalPages }, (_, indicatorIndex) => (
                  <span
                    key={`news-indicator-${indicatorIndex}`}
                    className={`w-1.5 h-1.5 rounded-full ${
                      indicatorIndex === currentPageSafe - 1 ? 'bg-white' : 'bg-white/35'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="mt-3 h-[3.5rem] overflow-hidden">
          {hasMobileTitle ? (
            <p className="text-[13px] font-medium text-gray-900 leading-snug break-words">
              <span>{mobileTitle.trim()}</span>
              {mobileItem?.link && (
                <a
                  href={mobileItem.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('common.more_details', { defaultValue: 'Подробнее' })}
                  className="inline-flex align-middle ml-1 text-base leading-none text-[#EC8C8E] transition-opacity duration-300 hover:opacity-75"
                >
                  →
                </a>
              )}
            </p>
          ) : mobileItem?.link ? (
            <a
              href={mobileItem.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('common.more_details', { defaultValue: 'Подробнее' })}
              className="inline-flex self-start text-base leading-none text-[#EC8C8E] transition-opacity duration-300 hover:opacity-75"
            >
              →
            </a>
          ) : null}
        </div>
      </div>
    );

    return (
      <section className="section overflow-hidden mt-12 md:mt-16">
        <div className="container-custom">
          <div
            className="max-w-6xl mx-auto overflow-hidden"
            onPointerDown={isDesktop ? undefined : handlePointerDown}
            onPointerUp={isDesktop ? undefined : handlePointerUp}
            onPointerCancel={isDesktop ? undefined : (() => { swipeStartRef.current = null; })}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="loader" />
              </div>
            ) : isDesktop ? (
              slides.length <= DESKTOP_VISIBLE_COUNT ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {slides.map((item, index) => renderCard(item, index))}
                </div>
              ) : (
                <div>
                  <div className="mb-6 grid grid-cols-3 items-center gap-2">
                    <div className="justify-self-start">
                      {desktopStartIndex > 0 ? (
                        <button
                          onClick={handleDesktopPrev}
                          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                        >
                          ← {t('common.back_short', { defaultValue: 'Назад' })}
                        </button>
                      ) : (
                        <span className="inline-block h-9" />
                      )}
                    </div>

                    <div className="justify-self-center">
                      <span className="inline-block h-9" />
                    </div>

                    <div className="justify-self-end">
                      {desktopStartIndex < maxDesktopStartIndex ? (
                        <button
                          onClick={handleDesktopNext}
                          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                        >
                          {t('common.more_short', { defaultValue: 'Еще' })} →
                        </button>
                      ) : (
                        <span className="inline-block h-9" />
                      )}
                    </div>
                  </div>

                  <div className="overflow-hidden">
                    <div
                      className="flex gap-6 transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]"
                      style={{
                        transform: `translateX(calc(-${desktopStartIndex} * ((100% - 3rem) / 3 + 1.5rem)))`,
                        willChange: 'transform'
                      }}
                    >
                      {slides.map((item, index) =>
                        renderCard(item, index, 'min-w-0 flex-shrink-0 basis-[calc((100%-3rem)/3)]')
                      )}
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="grid grid-cols-1">
                {mobileBody}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  const SiteStatsSection = () => {
    const navigate = useNavigate();

    const { data: lessonsData } = useQuery(
      ['landing-lessons-count'],
      async () => {
        try {
          return await lessonsAPI.getPublicLessons();
        } catch (err) {
          return { data: { data: [] } };
        }
      },
      {
        staleTime: 5 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      }
    );

    const { data: shopData } = useQuery(
      ['landing-products-count'],
      async () => {
        try {
          return await shopAPI.getProducts({ active: 'true' });
        } catch (err) {
          return { data: { data: [] } };
        }
      },
      {
        staleTime: 5 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      }
    );

    const lessons = Array.isArray(lessonsData?.data?.data) ? lessonsData.data.data : [];
    const products = Array.isArray(shopData?.data?.data) ? shopData.data.data : [];
    const lessonsCount = getLessonsCounterValue(lessons);

    return (
      <section className="section pt-0 pb-8 md:pb-12">
        <div className="container-custom">
	          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-6">
	            <button
	              type="button"
	              onClick={() => navigate('/training')}
              className="relative rounded-2xl md:rounded-3xl px-5 py-5 text-white h-auto min-h-[200px] md:h-auto md:min-h-[200px] flex items-stretch justify-between gap-3 text-left transition-opacity hover:opacity-95 overflow-hidden"
	              style={{ backgroundColor: '#EC8C8E' }}
	            >
              <div className="flex h-full w-full flex-col gap-2 md:flex-row md:items-center md:justify-start md:gap-1 lg:gap-2">
                <div className="shrink-0 font-black tabular-nums leading-[0.85] tracking-[-0.06em] sm:tracking-[-0.08em] md:tracking-[-0.1em] text-[clamp(2.85rem,13.5vw,4.5rem)] sm:text-[clamp(3.3rem,12vw,5.4rem)] md:text-[clamp(4.5rem,10.5vw,9rem)] lg:text-[clamp(5.4rem,10.5vw,10.5rem)]">
                  {lessonsCount}
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-between gap-2 md:flex-none md:w-[clamp(11rem,28vw,16rem)] md:self-center md:justify-center md:gap-1">
                  <div className="w-full text-left text-[11px] leading-snug font-semibold tracking-wide text-white/95 sm:text-xs md:text-sm">
                    {t('landing.stats.lessonsCountLong', {
                      defaultValue: 'Уникальных уроков для новичков и продвинутых пользователей'
                    })}
                  </div>
                  <span aria-hidden="true" className="self-end text-xl md:text-3xl leading-none md:absolute md:right-5 md:bottom-5">
                    →
                  </span>
	                </div>
	              </div>
	            </button>

	            <button
	              type="button"
	              onClick={() => navigate('/shop')}
              className="relative rounded-2xl md:rounded-3xl px-5 py-5 text-white h-auto min-h-[200px] md:h-auto md:min-h-[200px] flex items-stretch justify-between gap-3 text-left transition-opacity hover:opacity-95 overflow-hidden"
	              style={{ backgroundColor: '#EC8C8E' }}
	            >
              <div className="flex h-full w-full flex-col gap-2 md:flex-row md:items-center md:justify-start md:gap-1 lg:gap-2">
                <div className="shrink-0 font-black tabular-nums leading-[0.85] tracking-[-0.06em] sm:tracking-[-0.08em] md:tracking-[-0.1em] text-[clamp(2.85rem,13.5vw,4.5rem)] sm:text-[clamp(3.3rem,12vw,5.4rem)] md:text-[clamp(4.5rem,10.5vw,9rem)] lg:text-[clamp(5.4rem,10.5vw,10.5rem)]">
                  {products.length}
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-between gap-2 md:flex-none md:w-[clamp(11rem,28vw,16rem)] md:self-center md:justify-center md:gap-1">
                  <div className="w-full text-left text-[11px] leading-snug font-semibold tracking-wide text-white/95 sm:text-xs md:text-sm">
                    {t('landing.stats.productsCountLong', {
                      defaultValue: 'Эксклюзивных товаров ручной работы, материалов и инструментов для домашней мастерской'
                    })}
                  </div>
                  <span aria-hidden="true" className="self-end text-xl md:text-3xl leading-none md:absolute md:right-5 md:bottom-5">
                    →
                  </span>
	                </div>
	              </div>
	            </button>
          </div>
        </div>
      </section>
    );
  };

  const PortfolioSection = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageDirection, setPageDirection] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
      const updateViewport = () => {
        if (typeof window === 'undefined') return;
        setIsMobile(window.innerWidth < 1024);
      };

      updateViewport();
      window.addEventListener('resize', updateViewport);

      return () => window.removeEventListener('resize', updateViewport);
    }, []);

    const { data: portfolioData, isLoading } = useQuery(
      ['portfolio'],
      () => portfolioAPI.getPortfolio({
        limit: 100
      }),
      {
        staleTime: 5 * 60 * 1000,
      }
    );

    const portfolio = portfolioData?.data?.data || [];
    const itemsPerPage = isMobile ? 6 : 8;
    const totalPages = Math.max(1, Math.ceil(portfolio.length / itemsPerPage));
    const currentPageSafe = Math.min(currentPage, totalPages);
    const paginatedPortfolio = portfolio.slice(
      (currentPageSafe - 1) * itemsPerPage,
      currentPageSafe * itemsPerPage
    );
    const portfolioPlaceholders = Array.from(
      { length: Math.max(0, itemsPerPage - paginatedPortfolio.length) },
      (_, index) => `portfolio-placeholder-${currentPageSafe}-${index}`
    );

    useEffect(() => {
      setPageDirection(0);
      setCurrentPage(1);
    }, [isMobile, portfolio.length]);

    const pageVariants = {
      enter: (direction) => ({
        x: direction > 0 ? '130%' : '-130%',
      }),
      center: {
        x: '0%',
      },
      exit: (direction) => ({
        x: direction > 0 ? '-130%' : '130%',
      }),
    };

    const handleNextPage = () => {
      setPageDirection(1);
      setCurrentPage((prev) => Math.min(totalPages, prev + 1));
    };

    const handlePrevPage = () => {
      setPageDirection(-1);
      setCurrentPage((prev) => Math.max(1, prev - 1));
    };

    const canGoPrev = totalPages > 1 && currentPageSafe > 1;
    const canGoNext = totalPages > 1 && currentPageSafe < totalPages;

    return (
      <section id="portfolio" className="section">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto mb-6 grid grid-cols-3 items-center gap-2 min-h-9">
            <div className="justify-self-start">
              <button
                onClick={handlePrevPage}
                disabled={!canGoPrev}
                className={`inline-flex items-center text-sm transition-opacity ${
                  canGoPrev
                    ? 'text-gray-600 hover:text-gray-900 opacity-100'
                    : 'text-gray-600 opacity-0 pointer-events-none'
                }`}
              >
                ← {t('common.back_short', { defaultValue: 'Назад' })}
              </button>
            </div>

            <div className="justify-self-center text-xl md:text-2xl lg:text-[1.8rem] uppercase font-bold text-gray-900 text-center">
              {t('portfolio.title')}
            </div>

            <div className="justify-self-end">
              <button
                onClick={handleNextPage}
                disabled={!canGoNext}
                className={`inline-flex items-center text-sm transition-opacity ${
                  canGoNext
                    ? 'text-gray-600 hover:text-gray-900 opacity-100'
                    : 'text-gray-600 opacity-0 pointer-events-none'
                }`}
              >
                {t('common.more_short', { defaultValue: 'Еще' })} →
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="loader"></div>
            </div>
          ) : portfolio.length === 0 ? (
            <div className="text-center py-20">
              <div className="max-w-md mx-auto">
                <p className="text-2xl text-gray-600 mb-4">
                  {t('portfolio.empty_message')}
                </p>
                <p className="text-gray-500 text-sm">
                  {t('portfolio.subtitle')}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="max-w-6xl mx-auto overflow-hidden">
                <AnimatePresence initial={false} custom={pageDirection} mode="popLayout">
                  <motion.div
                    key={currentPageSafe}
                    custom={pageDirection}
                    variants={pageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.55, ease: [0.32, 0.72, 0, 1] }}
                    style={{ willChange: 'transform' }}
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
                  >
                    {paginatedPortfolio.map((item) => (
                      <div
                        key={item.id}
                        className="cursor-pointer flex flex-col"
                        onClick={() => navigate(`/portfolio/${item.id}`)}
                      >
                        <div className="relative rounded-2xl overflow-hidden aspect-square bg-gray-50 mb-3">
                          {(() => {
                            const mainIndex = item.mainImageIndex || 0;
                            const raw = item.images && item.images[mainIndex] ? item.images[mainIndex] : item.images && item.images[0];
                            const settings = Array.isArray(item.imageSettings) ? (item.imageSettings[mainIndex] || item.imageSettings[0] || {}) : {};
                            const zoom = Number.isFinite(Number(settings.zoom)) && Number(settings.zoom) >= 1 ? Number(settings.zoom) : 1;
                            const posX = Number.isFinite(Number(settings.x)) ? Math.min(100, Math.max(0, Number(settings.x))) : 50;
                            const posY = Number.isFinite(Number(settings.y)) ? Math.min(100, Math.max(0, Number(settings.y))) : 50;
                            const src = (() => {
                              if (!raw) return '/images/web-images/placeholder.jpg';
                              if (raw.startsWith('http')) return raw;
                              const trimmed = raw.replace(/^\/+/, '');
                              const withoutPrefix = trimmed.replace(/^uploads\//, '');
                              const normalized = `/uploads/${withoutPrefix}`;
                              return getImageUrl(normalized) || '/images/web-images/placeholder.jpg';
                            })();

                            return (
                          <OptimizedImage
                            src={src}
                            alt={typeof item.title === 'object'
                              ? (item.title[i18n.language] || item.title.ru || item.title.en || Object.values(item.title)[0] || 'Portfolio item')
                              : (item.title || 'Portfolio item')
                            }
                            className="w-full h-full object-cover"
                            width={400}
                            height={400}
                            priority
                            style={{
                              objectPosition: `${posX}% ${posY}%`,
                              transform: `scale(${zoom})`,
                              transformOrigin: `${posX}% ${posY}%`
                            }}
                          />
                            );
                          })()}
                        </div>

                        <div className="flex-1 flex flex-col gap-2 text-left">
                          <h3 className="text-[13px] font-medium text-gray-900 leading-snug">
                            {typeof item.title === 'object'
                              ? (item.title[i18n.language] || item.title.ru || item.title.en || Object.values(item.title)[0])
                              : item.title || 'Без названия'
                            }
                          </h3>
                        </div>
                      </div>
                    ))}

                    {portfolioPlaceholders.map((placeholderKey) => (
                      <div
                        key={placeholderKey}
                        className="flex flex-col pointer-events-none"
                        aria-hidden="true"
                      >
                        <div className="relative rounded-2xl overflow-hidden aspect-square bg-gray-50 mb-3 opacity-0" />
                        <div className="flex-1 flex flex-col gap-2 text-left">
                          <h3 className="text-[13px] font-medium leading-snug opacity-0">placeholder</h3>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>

            </>
          )}

        </div>
      </section>
    );
  };

  return (
    <>
      <Helmet>
        <title>Elena Fiore🌸</title>
        <meta name="description" content={t('hero.description')} />
        <meta property="og:title" content={`${t('hero.title')} - ${t('hero.subtitle')}`} />
        <meta property="og:description" content={t('hero.description')} />
      </Helmet>

      <style>{`.landing-root * { box-shadow: none !important; }`}</style>
      <div
        className="landing-root relative min-h-screen isolate bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "linear-gradient(to bottom right, rgba(253, 242, 248, 0.9), rgba(250, 245, 255, 0.9)), url('/images/web-images/back.svg')"
        }}
      >
        <div className="relative z-10">
          <NewsSection />
          <SiteStatsSection />
          <PortfolioSection />
          <ReviewsSection />
        </div>
      </div>
    </>
  );
};

export default Landing;
